import React, { useState, useEffect } from 'react';
import './OnlineQuranClassesInNewyork.css';
import { submitForm } from '../utils/formSubmit';
import Swal from 'sweetalert2';

const OnlineQuranClassesInNewyork = () => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    message: ''
  });

  // Animated counters state
  const [experience, setExperience] = useState(0);
  const [students, setStudents] = useState(0);
  const [tutors, setTutors] = useState(0);

  // FAQ active state mapping (all closed by default)
  const [activeFaqs, setActiveFaqs] = useState({});

  const toggleFaq = (index) => {
    setActiveFaqs(prev => {
      const isOpen = prev[index];
      return isOpen ? {} : { [index]: true };
    });
  };

  // Testimonials Slider State
  const [currentSlide, setCurrentSlide] = useState(0);
  const testimonials = [
    {
      text: "I was anxious about my kids' Quranic journey, then I found Al Quran Lesson in New York. The tutors are so polite with the children. The schedules are very flexible, so the kids easily balance school with their Quran Tajweed classes.",
      author: "Aisha Tahir - Brooklyn"
    },
    {
      text: "I enrolled to enhance my Quran recitation skills, and the Tajweed classes have been fantastic. The one-on-one instruction enabled me to correct faults fast. Highly recommended for those seeking serious Quranic education in New York.",
      author: "Yusuf M. Queens"
    },
    {
      text: "My children appreciate their online Quran courses and look forward to each session. The teachers are highly knowledgeable about almost every topic in Islamic history. I'm happy that I have discovered the best online Quran academy in New York.",
      author: "Mariam - Manhattan"
    },
    {
      text: "Everything from the free trial to the regular classes has gone smoothly and efficiently. The tutors communicate effectively and concentrate on actual development. One of the top online Quran programs in New York.",
      author: "Ibrahim - Staten Island"
    }
  ];

  // Auto-play timer for testimonials (5 seconds)
  useEffect(() => {
    const slideTimer = setInterval(() => {
      setCurrentSlide(prev => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(slideTimer);
  }, [testimonials.length]);

  const nextSlide = () => {
    setCurrentSlide(prev => (prev + 1) % testimonials.length);
  };

  const prevSlide = () => {
    setCurrentSlide(prev => (prev - 1 + testimonials.length) % testimonials.length);
  };

  // Contact Form State
  const [contactData, setContactData] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    country: '',
    course: '',
    message: '',
    consent: false
  });

  const handleContactChange = (e) => {
    const { name, value, type, checked } = e.target;
    setContactData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    if (!contactData.consent) {
      Swal.fire({
        title: 'Consent Required',
        text: "Please consent to terms to proceed.",
        icon: 'warning',
        confirmButtonColor: '#5c9456'
      });
      return;
    }
    try {
      await submitForm('New York Contact Form', contactData);
      Swal.fire({
        title: 'Success!',
        text: `Thank you, ${contactData.firstName} ${contactData.lastName}! We have received your message.`,
        icon: 'success',
        confirmButtonColor: '#5c9456'
      });
      setContactData({
        firstName: '',
        lastName: '',
        phone: '',
        country: '',
        course: '',
        message: '',
        consent: false
      });
    } catch (error) {
      Swal.fire({
        title: 'Error!',
        text: 'Failed to submit form. Please try again.',
        icon: 'error',
        confirmButtonColor: '#5c9456'
      });
    }
  };

  useEffect(() => {
    // Load Google reCAPTCHA v2 API script dynamically if not already loaded
    const scriptId = 'google-recaptcha-script';
    if (!document.getElementById(scriptId)) {
      const script = document.createElement('script');
      script.id = scriptId;
      script.src = 'https://www.google.com/recaptcha/api.js';
      script.async = true;
      script.defer = true;
      document.body.appendChild(script);
    }

    // Experience counter (0 to 16)
    let expStart = 0;
    const expEnd = 16;
    const expDuration = 1200; // ms
    const expStepTime = Math.max(Math.floor(expDuration / expEnd), 15);
    const expTimer = setInterval(() => {
      expStart += 1;
      setExperience(expStart);
      if (expStart >= expEnd) {
        clearInterval(expTimer);
      }
    }, expStepTime);

    // Tutors counter (0 to 100)
    let tutorsStart = 0;
    const tutorsEnd = 100;
    const tutorsDuration = 1200; // ms
    const tutorsStep = 2;
    const tutorsStepTime = Math.max(Math.floor(tutorsDuration / (tutorsEnd / tutorsStep)), 15);
    const tutorsTimer = setInterval(() => {
      tutorsStart += tutorsStep;
      setTutors(tutorsStart);
      if (tutorsStart >= tutorsEnd) {
        clearInterval(tutorsTimer);
      }
    }, tutorsStepTime);

    // Students counter (0 to 5000)
    let studentsStart = 0;
    const studentsEnd = 5000;
    const studentsDuration = 1200; // ms
    const studentsStep = 100;
    const studentsStepTime = Math.max(Math.floor(studentsDuration / (studentsEnd / studentsStep)), 15);
    const studentsTimer = setInterval(() => {
      studentsStart += studentsStep;
      setStudents(studentsStart);
      if (studentsStart >= studentsEnd) {
        clearInterval(studentsTimer);
      }
    }, studentsStepTime);

    return () => {
      clearInterval(expTimer);
      clearInterval(tutorsTimer);
      clearInterval(studentsTimer);
    };
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await submitForm('New York Registration Form', formData);
      Swal.fire({
        title: 'Success!',
        text: `Thank you, ${formData.name}! Your request has been sent successfully. We will contact you soon.`,
        icon: 'success',
        confirmButtonColor: '#5c9456'
      });
      setFormData({ name: '', phone: '', message: '' });
    } catch (error) {
      Swal.fire({
        title: 'Error!',
        text: 'Failed to submit form. Please try again.',
        icon: 'error',
        confirmButtonColor: '#5c9456'
      });
    }
  };

  return (
    <div className="ny-page-wrapper">
      {/* Hero Section */}
      <section
        className="ny-hero"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.75), rgba(0, 0, 0, 0.75)), url('${process.env.PUBLIC_URL}/assets/images/online-quran-classes-in-newyork/Online-Quran-lesson-in-Texas-1-scaled.webp')`
        }}
      >
        <div className="ny-hero__container">
          {/* Left Column: Heading, description, and stats */}
          <div className="ny-hero__content">
            <h1 className="ny-hero__title">
              <span className="ny-hero__title--highlight">Online Quran Classes In New York</span>
              <span className="ny-hero__title--divider"> | </span>
              <span className="ny-hero__title--main">Learn Quran Online With Tajweed Online</span>
            </h1>
            <p className="ny-hero__desc">
              Al Quran Lesson provides the best online quran classes in New York with experienced male and female tutors. We help students to learn the Quran online in a productive and safe environment. Our courses are designed for children and people of all ages. We have 16+ years of teaching experience, and we provide a safe and productive online learning environment for students of all age groups. New Yorkers can register today and start with a 3-day free trial to evaluate our teaching approach.
            </p>

            {/* Stats list */}
            <div className="ny-hero__stats">
              <div className="ny-stat-item">
                <span className="ny-stat-value">{experience} +</span>
                <span className="ny-stat-label">Years Of Experience</span>
              </div>
              <div className="ny-stat-item">
                <span className="ny-stat-value">{students.toLocaleString()} +</span>
                <span className="ny-stat-label">Students Studied</span>
              </div>
              <div className="ny-stat-item">
                <span className="ny-stat-value">{tutors}+</span>
                <span className="ny-stat-label">Expert Tutors</span>
              </div>
            </div>
          </div>

          {/* Right Column: Sign Up Form Card */}
          <div className="ny-hero__form-card">
            <div className="ny-form-header">
              <h4 className="ny-form-title">GET STARTED</h4>
              <h4 className="ny-form-subtitle">SIGN UP FOR FREE</h4>
            </div>

            <form onSubmit={handleSubmit} className="ny-form">
              <div className="ny-form-row">
                <div className="ny-form-group">
                  <input
                    type="text"
                    name="name"
                    placeholder="Name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="ny-form-input"
                  />
                </div>
                <div className="ny-form-group">
                  <input
                    type="tel"
                    name="phone"
                    placeholder="Phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="ny-form-input"
                  />
                </div>
              </div>

              <div className="ny-form-group">
                <textarea
                  name="message"
                  placeholder="Message"
                  value={formData.message}
                  onChange={handleChange}
                  className="ny-form-textarea"
                />
              </div>

              {/* reCAPTCHA integration */}
              <div className="ny-recaptcha-container">
                <div
                  className="g-recaptcha"
                  data-sitekey={
                    window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
                      ? '6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI' // Public Google test key for localhost development
                      : '6LdiHz4tAAAAAONg4UJVDRkG1K3enavhCUQbnmLx' // Production key for alquranlesson.com
                  }
                ></div>
              </div>

              <button type="submit" className="ny-form-submit-btn">
                Send
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* Intro Info Section */}
      <section className="ny-intro-section">
        <div className="ny-intro-container">
          {/* Left Column: Text & CTA */}
          <div className="ny-intro-content">
            <h2 className="ny-intro-title">
              <span className="ny-intro-title--green">Online Quran Classes With Tajweed</span> For Kids, Teenagers, and Adults In New York!
            </h2>
            <p className="ny-intro-desc">
              We offer <a href="/" className="ny-intro-link">online Quran classes for kids</a> in New York so that they fulfill their Quran learning needs, no matter the age group. The assigned tutors are very attentive as they give their full attention to a student during the learning phase. Parents may find a visible difference between students' progress with confidence in Quran recitation, understanding, fluency, and memorization.
            </p>
            <p className="ny-intro-desc">
              We have well-designed Quran classes in New York that can facilitate you with their personalized online sessions, flexible schedules, and thoughtfully designed curriculum. Whether you are enrolling your child or joining as an adult learner, our Quran academy ensures quality education aligned with proper Islamic principles. Many families consider us among the best online Quran classes in New York due to our consistent teaching standards and individualized learning model.
            </p>
            <a href="/free-trial/" className="ny-intro-btn">
              START FREE TRIAL CLASSES TODAY
            </a>
          </div>

          {/* Right Column: Image */}
          <div className="ny-intro-image-col">
            <img
              src={`${process.env.PUBLIC_URL}/assets/images/online-quran-classes-in-newyork/Al-Quran-Website-Image-02-scaled.webp`}
              alt="Online Quran Classes In New York"
              className="ny-intro-img"
            />
          </div>
        </div>
      </section>

      {/* Courses Offered Section */}
      <section className="ny-courses-section">
        <div className="ny-courses-container">
          <div className="ny-courses-header">
            <h2 className="ny-courses-badge">Courses Offered</h2>
            <h2 className="ny-courses-title">Online Quran Courses Available In New York</h2>
            <div className="ny-courses-divider">
              <img
                src={`${process.env.PUBLIC_URL}/assets/images/pshape.png`}
                alt="Divider"
                className="ny-courses-divider-img"
              />
            </div>
          </div>

          <div className="ny-courses-grid">
            {/* Left Column: Image */}
            <div className="ny-courses-image-col">
              <img
                src={`${process.env.PUBLIC_URL}/assets/images/online-quran-classes-in-newyork/steptodown.com907057-1.webp`}
                alt="15 Benefits Of Learning Quran Online"
                className="ny-courses-img"
              />
            </div>

            {/* Right Column: Course list */}
            <div className="ny-courses-list">
              {/* Course 1 */}
              <a href="/quran-reading-classes-online-for-kids-and-adults/" className="ny-course-item" style={{ textDecoration: 'none' }}>
                <span className="ny-course-icon-box">
                  <svg className="ny-course-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20M4 19.5A2.5 2.5 0 0 0 6.5 22H20M4 19.5V3.5A2.5 2.5 0 0 1 6.5 1M20 3.5V17" />
                  </svg>
                </span>
                <h3 className="ny-course-text">Quran Reading Classes Online</h3>
              </a>

              {/* Course 2 */}
              <a href="/online-tajweed-classes-tajweed-course/" className="ny-course-item" style={{ textDecoration: 'none' }}>
                <span className="ny-course-icon-box">
                  <svg className="ny-course-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 2a10 10 0 0 1 10 10c0 5.523-4.477 10-10 10S2 17.523 2 12A10 10 0 0 1 12 2z" />
                    <path d="M12 6v6l4 2" />
                  </svg>
                </span>
                <h3 className="ny-course-text">Quran Tajweed Classes</h3>
              </a>

              {/* Course 3 */}
              <a href="/quran-memorization-classes-course/" className="ny-course-item" style={{ textDecoration: 'none' }}>
                <span className="ny-course-icon-box">
                  <svg className="ny-course-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M3 21h18M12 2v19M7 21V10l5-4 5 4v11" />
                    <circle cx="12" cy="12" r="2" />
                  </svg>
                </span>
                <h3 className="ny-course-text">Quran Memorization Classes</h3>
              </a>

              {/* Course 4 */}
              <a href="/online-quran-translation-course/" className="ny-course-item" style={{ textDecoration: 'none' }}>
                <span className="ny-course-icon-box">
                  <svg className="ny-course-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
                    <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
                    <line x1="12" y1="22.08" x2="12" y2="12" />
                  </svg>
                </span>
                <h3 className="ny-course-text">Quran Translation Classes Online</h3>
              </a>

              {/* Course 5 */}
              <a href="/noorani-qaida-course/" className="ny-course-item" style={{ textDecoration: 'none' }}>
                <span className="ny-course-icon-box">
                  <svg className="ny-course-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
                    <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
                    <line x1="12" y1="22.08" x2="12" y2="12" />
                  </svg>
                </span>
                <h3 className="ny-course-text">Noorani Qaida Course</h3>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Section */}
      <section className="ny-why-section">
        <div className="ny-why-container">
          {/* Left Column: Text & CTA */}
          <div className="ny-why-content">
            <h2 className="ny-why-title">
              Why Students In New York Choose Our <span className="ny-why-title--green">Online Quran Classes?</span>
            </h2>
            <p className="ny-why-desc">
              Students and parents who are living in New York have trusted our Islamic education system for more than 16 years. They trust our Quran classes in New York, which focus on modern methods of recitation, proper Tajweed rules and pronunciation, and a smooth flow that provides learning progress under continuous tutor supervision.
            </p>
            <p className="ny-why-desc">
              Each student receives appreciation and structured guidance in return for their hard work, helping them gain confidence in reading and memorizing the Quran. Our learning environment is very supportive and admirable for spiritual growth while respecting individual learning pace.
            </p>
            <a href="/free-trial/" className="ny-why-btn">
              GET FREE TRIAL CLASSES
            </a>
          </div>

          {/* Right Column: Visual Stats Cards */}
          <div className="ny-why-stats-cards">
            {/* Card 1 */}
            <div className="ny-why-card">
              <div className="ny-why-card-left">
                <span className="ny-why-card-icon-box">
                  <svg className="ny-why-card-icon" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M20 6h-4V4c0-1.11-.89-2-2-2h-4c-1.11 0-2 .9-2 2v2H4c-1.11 0-1.99.89-1.99 2L2 19c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V8c0-1.11-.89-2-2-2zm-6 0h-4V4h4v2z" />
                  </svg>
                </span>
                <span className="ny-why-card-label">Years Of Experience</span>
              </div>
              <div className="ny-why-card-right-container">
                <img
                  src={`${process.env.PUBLIC_URL}/assets/images/online-quran-classes-in-newyork/right-image.webp`}
                  alt="Stats visual background"
                  className="ny-why-card-right-img"
                />
                <span className="ny-why-card-value">16+</span>
              </div>
            </div>

            {/* Card 2 */}
            <div className="ny-why-card">
              <div className="ny-why-card-left">
                <span className="ny-why-card-icon-box">
                  <svg className="ny-why-card-icon" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z" />
                  </svg>
                </span>
                <span className="ny-why-card-label">Students Studied</span>
              </div>
              <div className="ny-why-card-right-container">
                <img
                  src={`${process.env.PUBLIC_URL}/assets/images/online-quran-classes-in-newyork/right-image.webp`}
                  alt="Stats visual background"
                  className="ny-why-card-right-img"
                />
                <span className="ny-why-card-value">5000+</span>
              </div>
            </div>

            {/* Card 3 */}
            <div className="ny-why-card">
              <div className="ny-why-card-left">
                <span className="ny-why-card-icon-box">
                  <svg className="ny-why-card-icon" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 3L1 9l11 6 9-4.91V17h2V9L12 3zm0 18c-4.41 0-8-3.59-8-8c0-.62.08-1.21.21-1.79L9 15v-2.24l-3-2.14V7.57l6-3.43l6 3.43v3.05l-3 2.14V15l4.79-3.79c.13.58.21 1.17.21 1.79c0 4.41-3.59 8-8 8z" />
                  </svg>
                </span>
                <span className="ny-why-card-label">Expert Tutors</span>
              </div>
              <div className="ny-why-card-right-container">
                <img
                  src={`${process.env.PUBLIC_URL}/assets/images/online-quran-classes-in-newyork/right-image.webp`}
                  alt="Stats visual background"
                  className="ny-why-card-right-img"
                />
                <span className="ny-why-card-value">100+</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Cards Parallax Section */}
      <section
        className="ny-features-cards-section"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.75), rgba(0, 0, 0, 0.75)), url('${process.env.PUBLIC_URL}/assets/images/online-quran-classes-in-newyork/thumb_16734896_cover_header.webp')`
        }}
      >
        <div className="ny-features-cards-container">
          <div className="ny-features-cards-divider">
            <img
              src={`${process.env.PUBLIC_URL}/assets/images/pshape.png`}
              alt="Divider"
              className="ny-features-cards-divider-img"
            />
          </div>

          <div className="ny-features-cards-grid">
            {/* Card 1 */}
            <div className="ny-feature-card-item">
              <div className="ny-feature-card-icon-circle">
                <svg className="ny-feature-card-icon-svg" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M20 18c1.1 0 1.99-.9 1.99-2L22 6c0-1.1-.9-2-2-2H4c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2H0v2h24v-2h-4zM4 6h16v10H4V6z" />
                </svg>
              </div>
              <h3 className="ny-feature-card-title">Well-Trained Quran Tutors</h3>
              <p className="ny-feature-card-desc">
                The tutors of our academy are professionally trained as well as experienced in providing online Quran education by using modern and unique teaching methodologies.
              </p>
            </div>

            {/* Card 2 */}
            <div className="ny-feature-card-item">
              <div className="ny-feature-card-icon-circle">
                <svg className="ny-feature-card-icon-svg" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M5 13.18v4L12 21l7-3.82v-4L12 17l-7-3.82zM12 3L1 9l11 6 9-4.91V17h2V9L12 3z" />
                </svg>
              </div>
              <h3 className="ny-feature-card-title">Attentive Quran Classes</h3>
              <p className="ny-feature-card-desc">
                We have personalized classes with the sense of each student's requirement, including <a href="/online-tajweed-classes-tajweed-course/" className="ny-feature-card-link">Quran Tajweed Classes</a> and Quran memorization classes.
              </p>
            </div>

            {/* Card 3 */}
            <div className="ny-feature-card-item">
              <div className="ny-feature-card-icon-circle">
                <svg className="ny-feature-card-icon-svg" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19 4h-1V2h-2v2H8V2H6v2H5c-1.11 0-1.99.9-1.99 2L3 20c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 16H5V10h14v10zm0-12H5V6h14v2z" />
                </svg>
              </div>
              <h3 className="ny-feature-card-title">Flexible Online Schedules</h3>
              <p className="ny-feature-card-desc">
                Students are free to select their class timing in our online Quran classes with their desired tutors. This ensures calm learning and professional education for your kid.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQs Section */}
      <section className="ny-faq-section">
        <div className="ny-faq-container">
          <h2 className="ny-faq-heading">
            FREQUENTLY ASKED QUESTIONS <span className="ny-faq-heading--green">(FAQS)</span>
          </h2>

          <div className="ny-faq-grid">
            {/* Left Column FAQs */}
            <div className="ny-faq-col">
              {/* FAQ 1 */}
              <div className={`ny-faq-item ${activeFaqs[0] ? 'active' : ''}`}>
                <button className="ny-faq-question" onClick={() => toggleFaq(0)}>
                  <span>What benefits do we get from your online Quran classes in New York?</span>
                  <svg className="ny-faq-icon" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M8 5v14l11-7z" />
                  </svg>
                </button>
                <div className="ny-faq-answer">
                  <div className="ny-faq-answer-inner">
                    You can learn from globally certified Quran tutors on a custom or your preferred schedule. We have 16+ years of experience in online Quran learning for students.
                  </div>
                </div>
              </div>

              {/* FAQ 2 */}
              <div className={`ny-faq-item ${activeFaqs[1] ? 'active' : ''}`}>
                <button className="ny-faq-question" onClick={() => toggleFaq(1)}>
                  <span>Does a teenager join your Quran classes in New York?</span>
                  <svg className="ny-faq-icon" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M8 5v14l11-7z" />
                  </svg>
                </button>
                <div className="ny-faq-answer">
                  <div className="ny-faq-answer-inner">
                    Yes, teenagers are also welcome in our online Quran classes. We designed curricula including Quran revision, authentic pronunciation, and Quran Hifz classes in New York for teenagers.
                  </div>
                </div>
              </div>

              {/* FAQ 3 */}
              <div className={`ny-faq-item ${activeFaqs[2] ? 'active' : ''}`}>
                <button className="ny-faq-question" onClick={() => toggleFaq(2)}>
                  <span>What makes your academy the best online quran academy in New York?</span>
                  <svg className="ny-faq-icon" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M8 5v14l11-7z" />
                  </svg>
                </button>
                <div className="ny-faq-answer">
                  <div className="ny-faq-answer-inner">
                    Our staff is well-trained, plus we offer individualized learning techniques, friendly online sessions, and 16+ years of experience.
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column FAQs */}
            <div className="ny-faq-col">
              {/* FAQ 4 */}
              <div className={`ny-faq-item ${activeFaqs[3] ? 'active' : ''}`}>
                <button className="ny-faq-question" onClick={() => toggleFaq(3)}>
                  <span>Do you provide Quran classes in New York or any other state?</span>
                  <svg className="ny-faq-icon" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M8 5v14l11-7z" />
                  </svg>
                </button>
                <div className="ny-faq-answer">
                  <div className="ny-faq-answer-inner">
                    Yes, we provide online Quran classes all over the USA, including New York, <a style={{ color: "#006100" }} href="/online-quran-classes-texas/">Texas</a>, and <a style={{ color: "#006100" }} href="/online-quran-classes-california/">California</a>. Our classes are specifically designed for all age groups, especially for young students and children, as we work effectively on a strong Quranic basis.
                  </div>
                </div>
              </div>

              {/* FAQ 5 */}
              <div className={`ny-faq-item ${activeFaqs[4] ? 'active' : ''}`}>
                <button className="ny-faq-question" onClick={() => toggleFaq(4)}>
                  <span>Are Quran Hifz lessons in New York available online?</span>
                  <svg className="ny-faq-icon" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M8 5v14l11-7z" />
                  </svg>
                </button>
                <div className="ny-faq-answer">
                  <div className="ny-faq-answer-inner">
                    Yes, we also offer <a style={{ color: "#006100" }} href="/quran-memorization-classes-course/">Quran memorization classes</a> to our students, where you can memorize the Quran easily, as per your ability to learn.
                  </div>
                </div>
              </div>

              {/* FAQ 6 */}
              <div className={`ny-faq-item ${activeFaqs[5] ? 'active' : ''}`}>
                <button className="ny-faq-question" onClick={() => toggleFaq(5)}>
                  <span>Can adults take Quran classes in New York?</span>
                  <svg className="ny-faq-icon" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M8 5v14l11-7z" />
                  </svg>
                </button>
                <div className="ny-faq-answer">
                  <div className="ny-faq-answer-inner">
                    Yes, our Quran classes in New York are perfect for beginner level as well as adult level, with convenient online scheduling.
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Best Platform Section */}
      <section
        className="ny-platform-section"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.75), rgba(0, 0, 0, 0.75)), url('${process.env.PUBLIC_URL}/assets/images/online-quran-classes-in-newyork/thumb_16734896_cover_header.webp')`
        }}
      >
        <div className="ny-platform-container">
          <h2 className="ny-platform-heading">
            The Best Platform To Learn Quran Online In New York With Tajweed
          </h2>
          <div className="ny-platform-divider">
            <img
              src={`${process.env.PUBLIC_URL}/assets/images/pshape.png`}
              alt="Divider"
              className="ny-platform-divider-img"
            />
          </div>

          <div className="ny-platform-grid">
            {/* Card 1: Focus */}
            <div className="ny-platform-card">
              <div className="ny-platform-card-icon-circle">
                <svg className="ny-platform-card-icon-svg" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 16h-2v-2h2v2zm0-4h-2V7h2v7z" />
                </svg>
              </div>
              <h3 className="ny-platform-card-title">Focus</h3>
              <p className="ny-platform-card-desc">
                Our Quran Tajweed classes in New York help students to learn Arabic pronunciation and the right method of recitation. Our tutor focuses on proper Tajweed rules while making the learning process joyous and delightful.
              </p>
            </div>

            {/* Card 2: Confidence */}
            <div className="ny-platform-card">
              <div className="ny-platform-card-icon-circle">
                <svg className="ny-platform-card-icon-svg" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm0 10.99h7c-.53 4.12-3.28 7.79-7 8.94V12H5V6.3l7-3.11v8.8z" />
                </svg>
              </div>
              <h3 className="ny-platform-card-title">Confidence</h3>
              <p className="ny-platform-card-desc">
                Students develop confidence by correctly reciting the Quran through our maintained classes. Daily recitation and good supervision are the promise of consistent progress with great self-confidence.
              </p>
            </div>

            {/* Card 3: Flexibility */}
            <div className="ny-platform-card">
              <div className="ny-platform-card-icon-circle">
                <svg className="ny-platform-card-icon-svg" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M10 9h4V6h3l-5-5-5 5h3v3zm-1 1H6V7l-5 5 5 5v-3h3v-4zm14 2l-5-5v3h-3v4h3v3l5-5zm-9 3h-4v3H7l5 5 5-5h-3v-3z" />
                </svg>
              </div>
              <h3 className="ny-platform-card-title">Flexibility</h3>
              <p className="ny-platform-card-desc">
                We have custom timetables, specially designed for kids and adults, which help them to learn at their own speed. Our class timings and lessons are flexible.
              </p>
            </div>

            {/* Card 4: Expertise */}
            <div className="ny-platform-card">
              <div className="ny-platform-card-icon-circle">
                <svg className="ny-platform-card-icon-svg" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z" />
                </svg>
              </div>
              <h3 className="ny-platform-card-title">Expertise</h3>
              <p className="ny-platform-card-desc">
                Our skilled tutors help students master Quran recitation and Tajweed regulations. With specialized support, students can be able to read the Quran precisely, fluently, and with a deeper comprehension.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Enroll Today Section */}
      <section className="ny-enroll-section">
        <div className="ny-enroll-container">
          {/* Left Column: Offset Image & Overlap Badge */}
          <div className="ny-enroll-image-col">
            <div className="ny-enroll-image-wrapper">

              <img
                src={`${process.env.PUBLIC_URL}/assets/images/online-quran-classes-in-newyork/Al-Quran-Website-Image-01.webp`}
                alt="Online Quran Classes"
                className="ny-enroll-img"
              />

            </div>
          </div>

          {/* Right Column: Text & CTA */}
          <div className="ny-enroll-content-col">
            <h2 className="ny-enroll-title">
              <span className="ny-enroll-title--green">Enroll Today</span> In the Most Experienced Quran Learning With Tajweed, 3-Day Free Trial Available!
            </h2>
            <p className="ny-enroll-desc">
              If you live in New York, you can easily enroll today and start initial learning classes by taking a 3-day free trial. You will facilitate our pre-planned courses from online Tajweed classes, Quran Hifz classes, <a href="/quran-translation-classes/" className="ny-enroll-link">Quran translation classes</a>, and tafseer classes with all comforting and learning strategies. The environment is friendly enough to motivate teenagers and encouraging for new students for better recitation practices and fluency in the Quran reading.
            </p>
            <p className="ny-enroll-desc">
              During these sessions, young students can easily improve their understanding, pronunciation, and confidence through Quran recitation. Enroll today to begin your journey with our Quran classes in New York.
            </p>
            <a href="/free-trial/" className="ny-enroll-btn">
              GET FREE TRIAL CLASSES
            </a>
          </div>
        </div>
      </section>

      {/* CTA Banner Section */}
      <section
        className="ny-cta-banner-section"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.6)), url('${process.env.PUBLIC_URL}/assets/images/online-quran-classes-in-newyork/green-bg.webp')`
        }}
      >
        <div className="ny-cta-banner-container">
          <h2 className="ny-cta-banner-text">
            Flexible Quran Classes With Tajweed In New York. Get a Free Trial!
          </h2>
          <a href="/free-trial/" className="ny-cta-banner-btn">
            Start Learning
          </a>
        </div>
      </section>

      {/* Testimonials Slider Section */}
      <section className="ny-testimonials-section">
        <div className="ny-testimonials-container">
          <h2 className="ny-testimonials-heading">
            What Parents and Students of New York Think About
          </h2>

          {/* Gold Stars */}
          <div className="ny-testimonials-stars">
            {"★★★★★".split("").map((star, i) => (
              <span key={i} className="ny-star-icon">{star}</span>
            ))}
          </div>

          {/* Slider Container */}
          <div className="ny-testimonials-slider-box">
            {/* Left Control */}
            <button className="ny-slider-control prev" onClick={prevSlide} aria-label="Previous slide">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="15 18 9 12 15 6" />
              </svg>
            </button>

            {/* Slider Content Wrapper */}
            <div className="ny-slider-content-wrapper">
              {testimonials.map((t, idx) => (
                <div
                  key={idx}
                  className={`ny-testimonial-slide ${idx === currentSlide ? 'active' : ''}`}
                >
                  <p className="ny-testimonial-text">
                    &ldquo;{t.text}&rdquo;
                  </p>
                  <span className="ny-testimonial-author">
                    {t.author}
                  </span>
                </div>
              ))}
            </div>

            {/* Right Control */}
            <button className="ny-slider-control next" onClick={nextSlide} aria-label="Next slide">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="9 18 15 12 9 6" />
              </svg>
            </button>
          </div>

          {/* Navigation Dots */}
          <div className="ny-testimonials-dots">
            {testimonials.map((_, idx) => (
              <button
                key={idx}
                className={`ny-dot-btn ${idx === currentSlide ? 'active' : ''}`}
                onClick={() => setCurrentSlide(idx)}
                aria-label={`Go to slide ${idx + 1}`}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Custom Contact Info & Form Section */}
      <section className="ny-contact-section">
        <div className="ny-contact-container">
          {/* Left Column: Contact Info */}
          <div className="ny-contact-info-col">
            <h4 className="ny-contact-info-title">CONTACT INFO</h4>
            <ul className="ny-contact-info-list">
              <li className="ny-contact-info-item">
                <span className="ny-contact-icon-box">
                  <svg className="ny-contact-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </span>
                <a href="https://maps.google.com/?q=Mississauga,+Ontario,+L5K,+1W2" target="_blank" rel="noopener noreferrer" className="ny-contact-text">Mississauga, Ontario, L5K, 1W2</a>
              </li>

              <li className="ny-contact-info-item">
                <span className="ny-contact-icon-box">
                  <svg className="ny-contact-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.94.725l.548 2.2a1 1 0 01-.321.988l-1.305.98a10.582 10.582 0 004.872 4.872l.98-1.305a1 1 0 01.988-.321l2.2.548a1 1 0 01.725.94V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                </span>
                <a href="tel:+16477385707" className="ny-contact-text">+1 647 738 5707 (Canada)</a>
              </li>

              <li className="ny-contact-info-item">
                <span className="ny-contact-icon-box">
                  <svg className="ny-contact-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.94.725l.548 2.2a1 1 0 01-.321.988l-1.305.98a10.582 10.582 0 004.872 4.872l.98-1.305a1 1 0 01.988-.321l2.2.548a1 1 0 01.725.94V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                </span>
                <a href="tel:+17164577657" className="ny-contact-text">+1 716 457 7657 (USA)</a>
              </li>

              <li className="ny-contact-info-item">
                <span className="ny-contact-icon-box">
                  <svg className="ny-contact-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.94.725l.548 2.2a1 1 0 01-.321.988l-1.305.98a10.582 10.582 0 004.872 4.872l.98-1.305a1 1 0 01.988-.321l2.2.548a1 1 0 01.725.94V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                </span>
                <a href="tel:+447441394281" className="ny-contact-text">+44 7441 394281 (UK)</a>
              </li>

              <li className="ny-contact-info-item">
                <span className="ny-contact-icon-box">
                  <svg className="ny-contact-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </span>
                <a href="mailto:info@alquranlesson.com" className="ny-contact-text">info@alquranlesson.com</a>
              </li>
            </ul>
          </div>

          {/* Right Column: Connect Form */}
          <div className="ny-contact-form-col">
            <h4 className="ny-contact-form-title">CONNECT WITH US TODAY</h4>
            <form
              onSubmit={handleContactSubmit}
              className="ny-contact-form"
              style={{
                backgroundImage: `linear-gradient(rgba(255, 255, 255, 0.93), rgba(255, 255, 255, 0.93)), url('${process.env.PUBLIC_URL}/assets/images/green-bg.png')`,
              }}
            >
              {/* Row 1: First Name | Last Name | Phone No */}
              <div className="ny-contact-form-row three-cols">
                <input
                  type="text"
                  name="firstName"
                  placeholder="First Name"
                  value={contactData.firstName}
                  onChange={handleContactChange}
                  required
                  className="ny-contact-input"
                />
                <input
                  type="text"
                  name="lastName"
                  placeholder="Last Name"
                  value={contactData.lastName}
                  onChange={handleContactChange}
                  required
                  className="ny-contact-input"
                />
                <input
                  type="tel"
                  name="phone"
                  placeholder="Phone No"
                  value={contactData.phone}
                  onChange={handleContactChange}
                  required
                  className="ny-contact-input"
                />
              </div>

              {/* Row 2: Course & Country in one row */}
              <div className="ny-contact-form-row two-cols">
                <select
                  name="course"
                  value={contactData.course}
                  onChange={handleContactChange}
                  required
                  className="ny-contact-select"
                >
                  <option value="">Course</option>
                  <option value="Noorani Qaida">Noorani Qaida</option>
                  <option value="Quran with Tajweed">Quran with Tajweed</option>
                  <option value="Quran Memorization">Quran Memorization</option>
                  <option value="Quran Translation">Quran Translation</option>
                  <option value="Arabic Grammar">Arabic Grammar</option>
                </select>
                <select
                  name="country"
                  value={contactData.country}
                  onChange={handleContactChange}
                  required
                  className="ny-contact-select"
                >
                  <option value="">Country</option>
                  <option value="USA">USA</option>
                  <option value="Canada">Canada</option>
                  <option value="UK">UK</option>
                  <option value="Australia">Australia</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              {/* Row 4: Message */}
              <div className="ny-contact-field-full">
                <textarea
                  name="message"
                  placeholder="Message"
                  value={contactData.message}
                  onChange={handleContactChange}
                  className="ny-contact-textarea"
                />
              </div>

              {/* Checkbox Consent */}
              <div className="ny-contact-consent">
                <label className="ny-contact-consent-label">
                  <input
                    type="checkbox"
                    name="consent"
                    checked={contactData.consent}
                    onChange={handleContactChange}
                    required
                    className="ny-contact-checkbox"
                  />
                  <span>
                    By submitting this form, you consent to receiving text messages and emails from Al Quran Lesson about your inquiry regarding Quran lessons. A representative will reach out to discuss your needs and provide further details. The frequency of messages may vary. You can unsubscribe at any time by replying 'STOP' to text messages or clicking the unsubscribe link in emails. Standard message and data rates may apply.
                  </span>
                </label>
              </div>

              <div className="ny-contact-recaptcha-wrapper">
                <div
                  className="g-recaptcha"
                  data-sitekey={
                    window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
                      ? '6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI'
                      : '6LdiHz4tAAAAAONg4UJVDRkG1K3enavhCUQbnmLx'
                  }
                ></div>
              </div>

              <button type="submit" className="ny-contact-submit-btn">
                Submit
              </button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};

export default OnlineQuranClassesInNewyork;

